import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bool, func, objectOf, shape, string } from "prop-types";

import { RecoveryUserpassStep1 } from "@ui-kit/components/index.es";
import { actions as layoutActions } from "reducers/layout";
import { actions as recoveryPasswordActions, selectors as recoveryPasswordSelectors } from "reducers/recoveryPassword";
import { actions as fingerprintActions, selectors as fingerprintSelectors } from "reducers/fingerprint";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { isMobileNative } from "util/device";
import Yup from "yup";

const FORM_ID = "recoveryPassword.step1";

function RecoveryUserpassStep1V3({
    dispatch,
    history,
    userMaskedEmail,
    userMaskedPhone,
    userName,
    isRecoveryPassAndUserpass,
    recoveryAsyncPending,
    fieldErrors,
    fingerprintAvailable,
    isTokenSeedAvailable,
    fetchingToken,
}) {
    useEffect(() => {
        dispatch(layoutActions.setScopes(["recoveryUserpass"]));
        if (isMobileNative) {
            dispatch(fingerprintActions.fingerprintAvailability());
            dispatch(tokenActions.verifyTokenSeedExist());
        }
        return () => {
            dispatch(layoutActions.setScopes([]));
        };
    }, [dispatch]);

    const showBiometricBtn = useMemo(() => {
        if (!isMobileNative) {
            return false;
        }
        const useOfflineToken = configUtils.getBoolean("backend.show.Token.offline");
        const useBiometricTokenAccess = configUtils.getBoolean("backend.show.Biometric.Token.offline");
        const fingerprintEnabled = configUtils.getBoolean("frontend.login.fingerprint.enabled");
        return useOfflineToken && fingerprintAvailable && fingerprintEnabled && useBiometricTokenAccess;
    }, [fingerprintAvailable]);

    const channelOptions = useMemo(
        () => [
            { value: "phone", label: i18n.get(`${FORM_ID}.device.phone`), maskedData: userMaskedPhone || "" },
            { value: "email", label: i18n.get(`${FORM_ID}.device.email`), maskedData: userMaskedEmail || "" },
        ],
        [userMaskedEmail, userMaskedPhone],
    );

    const initialValues = useMemo(
        () => ({
            sendChannel: "phone",
            secondFactor: "",
        }),
        [],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                sendChannel: Yup.string().required(i18n.get(`${FORM_ID}.sendChannel.required`)),
                ...(!showBiometricBtn
                    ? {
                          secondFactor: Yup.string().required(
                              i18n.get("settings.token.disabledTokenPass.password.required"),
                          ),
                      }
                    : {}),
            }),
        [showBiometricBtn],
    );

    const handleBiometricSubmit = useCallback(
        (_e, formikProps) => {
            if (isTokenSeedAvailable) {
                const form = {
                    setValues: formikProps.setValues,
                    values: formikProps.values,
                };
                const field = { name: "secondFactor" };
                dispatch(tokenActions.generateBiometricsToken(formikProps.submitForm, form, field));
            } else {
                history.push("/v3/recoveryPassword/informationBiometrics");
            }
        },
        [dispatch, isTokenSeedAvailable, history],
    );

    const handleSubmit = useCallback(
        (values, formikBag) => {
            const channel = typeof values.sendChannel === "object" ? values.sendChannel?.value : values.sendChannel;
            const secondFactor = values.secondFactor ?? "";

            const enhancedBag = {
                ...formikBag,
                props: {
                    ...(formikBag.props || {}),
                    location: { pathname: "/v3/recoveryUserpass/step1" },
                    history,
                    isMobileNative,
                },
            };

            dispatch(
                recoveryPasswordActions.recoveryUserpassStep1(
                    channel,
                    userName || "",
                    secondFactor,
                    enhancedBag,
                    isRecoveryPassAndUserpass,
                ),
            );
        },
        [dispatch, history, userName, isRecoveryPassAndUserpass],
    );

    const handleHaveCode = useCallback(() => {
        history.push("/v3/recoveryUserpass/step2");
    }, [history]);

    const handleNoToken = useCallback(() => {
        history.push("/v3/recoveryPassword/informationClaveToken");
    }, [history]);

    const handleNoneOfTheAbove = useCallback(() => {
        history.push("/v3/recoveryPassword/information");
    }, [history]);

    const getLabels = useCallback((id) => {
        const keys = {
            sendChannel: `${FORM_ID}.sendChannel`,
            secondFactor: "recoveryPassword.step1.secondFactor.label",
            continue: "global.continue",
            haveCode: "recoveryPassword.step1.userHasCode",
            noAccess: "recoveryPassword.step1.device.none",
            noToken: "recoveryPassword.step1.token.V3.none",
            biometric: "biometrics.recovery.label",
        };
        return i18n.get(keys[id] ?? id);
    }, []);

    const getText = useCallback(
        (id) =>
            i18n.get(
                id === "recoveryUserpass.step1.title" && isRecoveryPassAndUserpass
                    ? "recoveryPassword.step1.title"
                    : id,
            ),
        [],
    );
    const headerKey = isRecoveryPassAndUserpass
        ? "recoveryUserpass.step1.forgotPasswordToo"
        : "login.step1.recoveryUser.header";

    return (
        <RecoveryUserpassStep1
            initialValues={initialValues}
            validationSchema={validationSchema}
            handleSubmit={handleSubmit}
            channelOptions={channelOptions}
            handleHaveCode={handleHaveCode}
            handleNoToken={handleNoToken}
            handleNoneOfTheAbove={handleNoneOfTheAbove}
            getLabels={getLabels}
            getText={getText}
            headerKey={headerKey}
            submitLoading={recoveryAsyncPending}
            fieldErrors={fieldErrors ?? undefined}
            enableBiometric={showBiometricBtn}
            handleBiometricSubmit={showBiometricBtn ? handleBiometricSubmit : undefined}
            biometricLoading={fetchingToken}
        />
    );
}

RecoveryUserpassStep1V3.propTypes = {
    dispatch: func.isRequired,
    history: shape({ push: func }).isRequired,
    userMaskedEmail: string,
    userMaskedPhone: string,
    userName: string,
    isRecoveryPassAndUserpass: bool,
    recoveryAsyncPending: bool,
    fieldErrors: objectOf(string),
    fingerprintAvailable: bool,
    isTokenSeedAvailable: bool,
    fetchingToken: bool,
};

RecoveryUserpassStep1V3.defaultProps = {
    userMaskedEmail: "",
    userMaskedPhone: "",
    userName: "",
    isRecoveryPassAndUserpass: false,
    recoveryAsyncPending: false,
    fieldErrors: undefined,
    fingerprintAvailable: false,
    isTokenSeedAvailable: false,
    fetchingToken: false,
};

const mapStateToProps = (state) => ({
    userMaskedEmail: recoveryPasswordSelectors.getMaskedEmail(state),
    userMaskedPhone: recoveryPasswordSelectors.getMaskedPhone(state),
    userName: recoveryPasswordSelectors.getUserName(state),
    isRecoveryPassAndUserpass: recoveryPasswordSelectors.getRecoveryPassAndUserpass(state),
    recoveryAsyncPending: recoveryPasswordSelectors.getAsyncPending(state),
    fieldErrors: recoveryPasswordSelectors.getFormFieldErrors(state) ?? undefined,
    fingerprintAvailable: fingerprintSelectors.getAvailability(state),
    isTokenSeedAvailable: tokenSelectors.getIsTokenSeedAvailable(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
});

export default connect(mapStateToProps)(RecoveryUserpassStep1V3);
